import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  BillSplitDetailResponseDto,
  BillSplitItemAssignmentsBulkResponseDto,
  BillSplitItemResponseDto,
  BillSplitItemsBulkResponseDto,
  BillSplitParticipantResponseDto,
  BillSplitParticipantsBulkResponseDto,
  BillSplitResponseDto,
  BillSplitSummaryResponseDto,
  CreateBillSplitItemRequestDto,
  CreateBillSplitItemsBulkRequestDto,
  CreateBillSplitParticipantRequestDto,
  CreateBillSplitParticipantsBulkRequestDto,
  CreateBillSplitRequestDto,
  ReplaceBillSplitItemAssignmentsBulkRequestDto,
  CreateBillSplitResponseDto,
  SplitBillSplitItemRequestDto,
  ReplaceBillSplitAdjustmentsRequestDto,
} from '@features/client/models/public-bill-split-models';
import { BillSplitInvitationResponseDto } from '@features/client/models/bill-split-invitation-models';

@Injectable({ providedIn: 'root' })
export class PublicBillSplitApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/public/bill-splits`;

  createSplit(body: CreateBillSplitRequestDto): Observable<CreateBillSplitResponseDto> {
    return this.http.post<CreateBillSplitResponseDto>(this.baseUrl, body);
  }

  getInvitation(splitId: string, hostToken: string): Observable<BillSplitInvitationResponseDto> {
    return this.http.get<BillSplitInvitationResponseDto>(`${this.baseUrl}/${splitId}/invitation`, {
      params: { hostToken },
    });
  }

  getSplitDetail(splitId: string): Observable<BillSplitDetailResponseDto> {
    return this.http.get<BillSplitDetailResponseDto>(`${this.baseUrl}/${splitId}`);
  }

  getSummary(splitId: string): Observable<BillSplitSummaryResponseDto> {
    return this.http.get<BillSplitSummaryResponseDto>(`${this.baseUrl}/${splitId}/summary`);
  }

  replaceAdjustments(
    splitId: string,
    request: ReplaceBillSplitAdjustmentsRequestDto,
  ): Observable<BillSplitResponseDto> {
    return this.http.patch<BillSplitResponseDto>(`${this.baseUrl}/${splitId}/adjustments`, request);
  }

  closeSplit(splitId: string): Observable<BillSplitResponseDto> {
    return this.http.patch<BillSplitResponseDto>(`${this.baseUrl}/${splitId}/close`, {});
  }

  addParticipant(
    splitId: string,
    body: CreateBillSplitParticipantRequestDto,
  ): Observable<BillSplitParticipantResponseDto> {
    return this.http.post<BillSplitParticipantResponseDto>(
      `${this.baseUrl}/${splitId}/participants`,
      body,
    );
  }

  addParticipantsBulk(
    splitId: string,
    body: CreateBillSplitParticipantsBulkRequestDto,
  ): Observable<BillSplitParticipantsBulkResponseDto> {
    return this.http.post<BillSplitParticipantsBulkResponseDto>(
      `${this.baseUrl}/${splitId}/participants/bulk`,
      body,
    );
  }

  addItemsBulk(
    splitId: string,
    body: CreateBillSplitItemsBulkRequestDto,
  ): Observable<BillSplitItemsBulkResponseDto> {
    return this.http.post<BillSplitItemsBulkResponseDto>(
      `${this.baseUrl}/${splitId}/items/bulk`,
      body,
    );
  }

  replaceAssignmentsBulk(
    splitId: string,
    body: ReplaceBillSplitItemAssignmentsBulkRequestDto,
  ): Observable<BillSplitItemAssignmentsBulkResponseDto> {
    return this.http.put<BillSplitItemAssignmentsBulkResponseDto>(
      `${this.baseUrl}/${splitId}/items/assignments/bulk`,
      body,
    );
  }

  updateParticipant(
    splitId: string,
    participantId: string,
    body: CreateBillSplitParticipantRequestDto,
  ): Observable<BillSplitParticipantResponseDto> {
    return this.http.patch<BillSplitParticipantResponseDto>(
      `${this.baseUrl}/${splitId}/participants/${participantId}`,
      body,
    );
  }

  deleteParticipant(splitId: string, participantId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${splitId}/participants/${participantId}`);
  }

  updateItem(
    splitId: string,
    itemId: string,
    body: CreateBillSplitItemRequestDto,
  ): Observable<BillSplitItemResponseDto> {
    return this.http.patch<BillSplitItemResponseDto>(
      `${this.baseUrl}/${splitId}/items/${itemId}`,
      body,
    );
  }

  deleteItem(splitId: string, itemId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${splitId}/items/${itemId}`);
  }

  splitItem(
    splitId: string,
    sourceItemId: string,
    body: SplitBillSplitItemRequestDto,
  ): Observable<BillSplitItemResponseDto[]> {
    return this.http.post<BillSplitItemResponseDto[]>(
      `${this.baseUrl}/${splitId}/items/${sourceItemId}/divide`,
      body,
    );
  }
}
